import { useMutation, useQuery } from "@apollo/client";
import { sortBy, uniqBy } from "lodash";
import { useMemo } from "react";
import {
  GetOrganizationCareerTrackRolesQuery,
  GetOrganizationCareerTrackRolesQueryVariables,
  SetMemberCareerRoleMutation,
  SetMemberCareerRoleMutationVariables,
} from "types/graphql-schema";

import { errorNotificationVar } from "@cache/cache";
import { assertEdgesNonNull } from "@helpers/helpers";

import getOrganizationCareerTrackRolesQuery from "../graphql/get-organization-career-track-roles-query";
import getOrganizationMemberQuery from "../graphql/get-organization-member-query";
import getOrganizationMembersQuery from "../graphql/get-organization-members-query";
import getPersonCareerQuery from "../graphql/get-person-career-query";
import setMemberCareerRoleMutation from "../graphql/set-member-career-role-mutation";

// Every role in the organization as a picker option, one line each: the
// role's name, with its track on the right. And the save that adds a person
// to one of them. Their other roles stay: the save ends only the ones named
// in endRoleIds.
const useCareerRoleOptions = (organizationId: number) => {
  const { data, loading } = useQuery<
    GetOrganizationCareerTrackRolesQuery,
    GetOrganizationCareerTrackRolesQueryVariables
  >(getOrganizationCareerTrackRolesQuery, { variables: { organizationId } });
  const [setRole, { loading: isSaving }] = useMutation<
    SetMemberCareerRoleMutation,
    SetMemberCareerRoleMutationVariables
  >(setMemberCareerRoleMutation, {
    // The People table's Role column, the edit dialog when it opens on its
    // own (the org chart's), and the person's career view.
    refetchQueries: [
      getOrganizationMembersQuery,
      getOrganizationMemberQuery,
      getPersonCareerQuery,
    ],
    onError: () =>
      errorNotificationVar({ title: "The role couldn't be changed." }),
  });

  const options = useMemo(
    () =>
      sortBy(uniqBy(data ? assertEdgesNonNull(data.careerRoles) : [], "id"), [
        (role) => role.careerTrack.title.toLowerCase(),
        (role) => role.level,
      ]).map((role) => ({
        label: role.title,
        value: role.id,
        htmlLabel: (
          <span className="flex flex-1 items-center justify-between gap-3 min-w-0">
            <span className="truncate">{role.title}</span>
            {/* Dimmed rather than recoloured, so it reads on a highlighted
                row too. */}
            <span className="shrink-0 opacity-60">
              {role.careerTrack.title}
            </span>
          </span>
        ),
      })),
    [data],
  );

  const assignRole = (
    userId: number,
    roleId: number | null,
    endRoleIds: number[] = [],
  ) => setRole({ variables: { organizationId, userId, roleId, endRoleIds } });

  return { options, loading, isSaving, assignRole };
};

export default useCareerRoleOptions;
